import { $ZodError } from "zod/v4/core";
import { flattenError, z } from "zod/v4-mini";
import { Logger } from "@/utils/logger";
import { type SDKTheme, defaultTheme, setTheme } from "@/utils/theme";

const SDKThemeColorsSchema = z.object({
  brand50: z.string(),
  brand100: z.string(),
  brand200: z.string(),
  brand300: z.string(),
  brand400: z.string(),
  brand500: z.string(),
  brand600: z.string(),
  brand700: z.string(),
  brand800: z.string(),
  brand900: z.string(),
  brandBackground: z.string(),
  brandText: z.string(),
  white: z.string(),
  accentLight: z.string(),
  accentDark: z.string(),
  overlayBackground: z.string(),
  toastBackground: z.string(),
  modalBackground: z.string(),
  textPrimary: z.string(),
  textSecondary: z.string(),
  borderLight: z.string(),
});

const SDKThemeFontsSchema = z.object({
  sans: z.string(),
});

const SDKThemeSchema = z.object({
  colors: SDKThemeColorsSchema,
  fonts: SDKThemeFontsSchema,
});

const SDKIframeOptionsSchema = z.object({
  backgroundColor: z.string(),
});

const SDKOptionsSchema = z.object({
  clientId: z.string(),
  apiBase: z.optional(z.string()),
  appOrigin: z.optional(z.string()),
  enableAutoHeightEmbed: z.optional(z.boolean()),
  enableToastNotifications: z.optional(z.boolean()),
  iframe: z.optional(SDKIframeOptionsSchema),
  theme: z.optional(SDKThemeSchema),
  trackingDisabledByDefault: z.optional(z.boolean()),
  hostLoginFn: z.optional(z.unknown()),
  hostRewardsFn: z.optional(z.unknown()),
  onAuthStateChange: z.optional(z.unknown()),
});

type SDKIframeOptions = z.infer<typeof SDKIframeOptionsSchema>;
type SDKBaseOptions = z.infer<typeof SDKOptionsSchema>;

const defaultIframeOptions = {
  backgroundColor: "var(--color-brand-background)",
} satisfies SDKIframeOptions;

const defaultOptions = {
  apiBase: "https://api.fanfest.vip",
  appOrigin: "https://fanfest.vip",
  enableAutoHeightEmbed: false,
  enableToastNotifications: true,
  iframe: defaultIframeOptions,
} satisfies Partial<SDKBaseOptions>;

export class SDKOptions {
  clientId: string;
  apiBase: string;
  appOrigin: string;
  enableAutoHeightEmbed: boolean;
  enableToastNotifications: boolean;
  iframe: SDKIframeOptions;
  theme: SDKTheme;
  trackingDisabledByDefault: boolean;
  hostLoginFn: (() => void) | undefined;
  hostRewardsFn: (() => void) | undefined;
  onAuthStateChange: ((authenticated: boolean) => void) | undefined;
  #logger: Logger;

  get graphqlUrl() {
    return `${this.apiBase}/graphql`;
  }

  constructor(options: unknown, logger: Logger) {
    this.#logger = logger.extend(this.constructor.name);

    try {
      const parsedOptions = SDKOptionsSchema.parse(options);
      const mergedOptions = { ...defaultOptions, ...parsedOptions };

      this.clientId = mergedOptions.clientId;
      this.apiBase = mergedOptions.apiBase;
      this.appOrigin = mergedOptions.appOrigin;
      this.enableAutoHeightEmbed = mergedOptions.enableAutoHeightEmbed;
      this.enableToastNotifications = mergedOptions.enableToastNotifications;
      this.iframe = mergedOptions.iframe;
      this.trackingDisabledByDefault =
        parsedOptions.trackingDisabledByDefault ?? false;

      // Theme: if provided and valid (all fields present), use it.
      // Otherwise fall back to the default theme.
      if (parsedOptions.theme) {
        this.theme = parsedOptions.theme;
        this.#logger.info("Using custom theme");
      } else {
        this.theme = defaultTheme;
      }
      setTheme(this.theme);

      if (parsedOptions.hostLoginFn instanceof Function) {
        // Ensure the function is bound to the window object to not expose the function to the global scope
        this.hostLoginFn = (mergedOptions.hostLoginFn as () => void).bind(
          window,
        );
      }

      if (parsedOptions.hostRewardsFn instanceof Function) {
        this.hostRewardsFn = (mergedOptions.hostRewardsFn as () => void).bind(
          window,
        );
      }

      if (parsedOptions.onAuthStateChange instanceof Function) {
        this.onAuthStateChange = (
          mergedOptions.onAuthStateChange as (authenticated: boolean) => void
        ).bind(window);
      }
    } catch (error) {
      if (error instanceof $ZodError) {
        this.#logger.error(
          "Error parsing SDK options",
          flattenError(error).fieldErrors,
        );
        throw new Error(this.#logger.prefixMessage("Invalid SDK options"));
      } else {
        throw error;
      }
    }
  }
}
